import React, { useCallback, useEffect, useState } from "react";
import {
  Bot,
  BrainCircuit,
  CalendarDays,
  Check,
  CircleAlert,
  Copy,
  Eye,
  EyeOff,
  KeyRound,
  Link2,
  LoaderCircle,
  PhoneCall,
  PlugZap,
  Power,
  RefreshCw,
  Save,
  Trash2,
  Webhook,
  X,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import fetchData from "../../api/fetchApi";
import { getLang } from "../../i18n";

function formatDate(value, isArabic) {
  if (!value) return isArabic ? "غير متاح" : "Not available";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(isArabic ? "ar-LY" : "en", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

function FeatureRow({ icon: Icon, title, description, active, busy, onToggle, isArabic }) {
  return (
    <div className="feature-row">
      <span className="metric-icon metric-slate"><Icon size={20} /></span>
      <div className="min-w-0 flex-1">
        <strong>{title}</strong>
        <p>{description}</p>
      </div>
      <button
        type="button"
        className={`switch-control ${active ? "switch-active" : ""}`}
        onClick={onToggle}
        disabled={busy}
        role="switch"
        aria-checked={active}
        aria-label={`${active ? (isArabic ? "إيقاف" : "Disable") : (isArabic ? "تفعيل" : "Enable")} ${title}`}
      >
        <span />
      </button>
    </div>
  );
}

export default function SessionStatus() {
  const isArabic = getLang() === "ar";
  const role = localStorage.getItem("role") || "client";
  const clientCanEditWebhook = !["admin", "superadmin"].includes(role);
  const sessionToken = localStorage.getItem("token") || "";
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [busyAction, setBusyAction] = useState("");
  const [showQr, setShowQr] = useState(false);
  const [qrValue, setQrValue] = useState("");
  const [qrLoading, setQrLoading] = useState(false);
  const [qrError, setQrError] = useState("");
  const [qrTimer, setQrTimer] = useState(60);
  const [showToken, setShowToken] = useState(false);
  const [tokenCopied, setTokenCopied] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState("");
  const [savedWebhookUrl, setSavedWebhookUrl] = useState("");
  const [webhookEnabled, setWebhookEnabled] = useState(false);
  const [webhookLoading, setWebhookLoading] = useState(false);
  const [webhookSaving, setWebhookSaving] = useState(false);
  const [webhookError, setWebhookError] = useState("");

  const loadSession = useCallback(async () => {
    setError("");
    try {
      const result = await fetchData("session");
      if (!result?.uuid || result?.status === "not_created") {
        setSession(null);
        setError(result?.message || "");
        return;
      }
      setSession(result);
      const currentWebhook = result?.web_hook || result?.webhook || "";
      setWebhookUrl(currentWebhook);
      setSavedWebhookUrl(currentWebhook);
      setWebhookEnabled(Boolean(result?.webhook_enabled && currentWebhook));
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSession();
  }, [loadSession]);

  useEffect(() => {
    if (!session?.uuid || !clientCanEditWebhook) return undefined;
    let active = true;
    setWebhookLoading(true);
    setWebhookError("");

    fetchData(`session/webhook?uuid=${encodeURIComponent(session.uuid)}`)
      .then((result) => {
        if (!active) return;
        const currentWebhook = result?.webhook_url || "";
        setWebhookUrl(currentWebhook);
        setSavedWebhookUrl(currentWebhook);
        setWebhookEnabled(Boolean(result?.enabled && currentWebhook));
      })
      .catch((requestError) => {
        if (active) setWebhookError(requestError.message);
      })
      .finally(() => {
        if (active) setWebhookLoading(false);
      });

    return () => {
      active = false;
    };
  }, [clientCanEditWebhook, session?.uuid]);

  const loadQr = useCallback(async () => {
    setQrLoading(true);
    setQrError("");
    setQrTimer(60);
    try {
      const result = await fetchData("session/qr");
      if (result?.qr) {
        setQrValue(result.qr);
      } else if (String(result?.status).toLowerCase() === "connected") {
        setQrValue("");
        setQrError(isArabic ? "الجلسة متصلة بالفعل ولا تحتاج إلى رمز جديد." : "This session is already connected.");
        await loadSession();
      } else {
        setQrValue("");
        setQrError(result?.message || (isArabic ? "رمز الربط غير جاهز. حاول مرة أخرى." : "The QR code is not ready. Try again."));
      }
    } catch (requestError) {
      setQrValue("");
      setQrError(requestError.message);
    } finally {
      setQrLoading(false);
    }
  }, [isArabic, loadSession]);

  useEffect(() => {
    if (!showQr) return undefined;
    const countdown = window.setInterval(() => {
      setQrTimer((current) => (current <= 1 ? 60 : current - 1));
    }, 1000);
    const refresh = window.setInterval(loadQr, 60000);
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setShowQr(false);
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      window.clearInterval(countdown);
      window.clearInterval(refresh);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [loadQr, showQr]);

  const openQr = () => {
    setShowQr(true);
    loadQr();
  };

  const disconnect = async () => {
    setBusyAction("disconnect");
    setError("");
    setSuccess("");
    try {
      await fetchData("session/stop");
      setSuccess(isArabic ? "تم قطع اتصال الجلسة." : "The session was disconnected.");
      await loadSession();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusyAction("");
    }
  };

  const toggleFeature = async (feature, active) => {
    if (!session?.uuid) return;
    setBusyAction(feature);
    setError("");
    setSuccess("");
    const action = active ? "deactivate" : "activate";
    try {
      await fetchData(`session/${feature}/${action}?uuid=${encodeURIComponent(session.uuid)}`, "PUT");
      setSuccess(
        feature === "autoresponse"
          ? active
            ? (isArabic ? "تم إيقاف الرد الآلي." : "Auto replies were disabled.")
            : (isArabic ? "تم تفعيل الرد الآلي." : "Auto replies were enabled.")
          : active
          ? (isArabic ? "تم إيقاف الرد الذكي." : "AI replies were disabled.")
          : (isArabic ? "تم تفعيل الرد الذكي." : "AI replies were enabled.")
      );
      await loadSession();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusyAction("");
    }
  };

  const copySessionToken = async () => {
    if (!sessionToken) return;
    try {
      await navigator.clipboard.writeText(sessionToken);
      setTokenCopied(true);
      window.setTimeout(() => setTokenCopied(false), 1800);
    } catch {
      setError(isArabic ? "تعذر نسخ الرمز. انسخه يدوياً." : "Could not copy the token. Copy it manually.");
    }
  };

  const validateWebhook = (value) => {
    if (!value) return "";
    if (value.length > 255) {
      return isArabic ? "يجب ألا يتجاوز الرابط 255 حرفاً." : "The URL cannot exceed 255 characters.";
    }

    try {
      const parsed = new URL(value);
      if (!["http:", "https:"].includes(parsed.protocol) || !parsed.hostname) {
        throw new Error("invalid protocol");
      }
      if (parsed.username || parsed.password || parsed.hash) {
        return isArabic
          ? "لا يمكن أن يحتوي الرابط على بيانات دخول أو جزء #."
          : "The URL cannot contain credentials or a # fragment.";
      }
    } catch {
      return isArabic
        ? "أدخل رابط HTTP أو HTTPS كاملاً وصحيحاً."
        : "Enter a complete, valid HTTP or HTTPS URL.";
    }
    return "";
  };

  const saveWebhook = async (event) => {
    event.preventDefault();
    if (!session?.uuid || !clientCanEditWebhook) return;
    const normalizedUrl = webhookUrl.trim();
    const validationError = validateWebhook(normalizedUrl);
    if (validationError) {
      setWebhookError(validationError);
      return;
    }

    setWebhookSaving(true);
    setWebhookError("");
    setError("");
    setSuccess("");
    try {
      const result = await fetchData(
        `session/webhook?uuid=${encodeURIComponent(session.uuid)}`,
        "PUT",
        JSON.stringify({ webhook_url: normalizedUrl })
      );
      const updatedUrl = result?.webhook_url || normalizedUrl;
      setWebhookUrl(updatedUrl);
      setSavedWebhookUrl(updatedUrl);
      setWebhookEnabled(Boolean(result?.enabled && updatedUrl));
      setSession((current) => current ? { ...current, web_hook: updatedUrl, webhook_enabled: Boolean(result?.enabled) } : current);
      setSuccess(isArabic ? "تم تحديث رابط Webhook." : "Webhook URL updated.");
    } catch (requestError) {
      setWebhookError(requestError.message);
    } finally {
      setWebhookSaving(false);
    }
  };

  const removeWebhook = async () => {
    if (!session?.uuid || !clientCanEditWebhook) return;
    setWebhookSaving(true);
    setWebhookError("");
    setError("");
    setSuccess("");
    try {
      await fetchData(
        `session/webhook?uuid=${encodeURIComponent(session.uuid)}`,
        "DELETE"
      );
      setWebhookUrl("");
      setSavedWebhookUrl("");
      setWebhookEnabled(false);
      setSession((current) => current ? { ...current, web_hook: "", webhook_enabled: false } : current);
      setSuccess(isArabic ? "تم حذف رابط Webhook وإيقاف الإرسال إليه." : "Webhook removed and delivery disabled.");
    } catch (requestError) {
      setWebhookError(requestError.message);
    } finally {
      setWebhookSaving(false);
    }
  };

  if (loading) {
    return <div className="panel-card flex min-h-72 items-center justify-center"><LoaderCircle className="animate-spin text-teal-700" size={28} /><span className="sr-only">Loading</span></div>;
  }

  if (!session) {
    return (
      <section className="empty-state">
        <span className="empty-state-icon"><CircleAlert size={25} /></span>
        <h2>{isArabic ? "لا توجد جلسة متاحة" : "No session available"}</h2>
        <p>{error || (isArabic ? "أنشئ جلسة للحساب من لوحة الإدارة أولاً." : "Create a session for this account from the admin service first.")}</p>
        <button type="button" className="secondary-button" onClick={() => { setLoading(true); loadSession(); }}><RefreshCw size={17} />{isArabic ? "إعادة المحاولة" : "Try again"}</button>
      </section>
    );
  }

  const connected = String(session.status).toLowerCase() === "connected";
  const currentWebhookUrl = savedWebhookUrl || session.web_hook || session.webhook || "";

  return (
    <div className="dashboard-stack">
      <section className="session-summary">
        <div className="session-identity">
          <span className={`session-phone ${connected ? "session-phone-online" : ""}`}><PhoneCall size={25} /></span>
          <div>
            <span className="eyebrow">{isArabic ? "جلسة واتساب" : "WhatsApp session"}</span>
            <h2>{session.session}</h2>
            <span className={`status-badge ${connected ? "status-badge-online" : "status-badge-offline"}`}>
              <span />{connected ? (isArabic ? "متصلة" : "Connected") : (isArabic ? "غير متصلة" : "Disconnected")}
            </span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" className="primary-button primary-button-auto" onClick={openQr} disabled={qrLoading}><PlugZap size={18} />{connected ? (isArabic ? "عرض حالة الربط" : "Connection status") : (isArabic ? "ربط واتساب" : "Connect WhatsApp")}</button>
          {connected && <button type="button" className="danger-button" onClick={disconnect} disabled={busyAction === "disconnect"}><Power size={18} />{busyAction === "disconnect" ? (isArabic ? "جاري القطع…" : "Disconnecting…") : (isArabic ? "قطع الاتصال" : "Disconnect")}</button>}
          <button type="button" className="icon-button" onClick={loadSession} aria-label={isArabic ? "تحديث الحالة" : "Refresh status"}><RefreshCw size={18} /></button>
        </div>
      </section>

      {error && <div className="notice notice-error" role="alert">{error}</div>}
      {success && <div className="notice notice-success" role="status">{success}</div>}

      <section className="grid grid-cols-1 gap-4 xl:grid-cols-[1.15fr_0.85fr]">
        <article className="panel-card">
          <div className="panel-heading"><div><span className="eyebrow">{isArabic ? "الأتمتة" : "Automation"}</span><h3>{isArabic ? "خصائص الجلسة" : "Session features"}</h3></div></div>
          <div className="feature-list">
            <FeatureRow icon={Bot} title={isArabic ? "الرد الآلي" : "Auto replies"} description={isArabic ? "يرد بقواعد الكلمات المفتاحية التي أعددتها." : "Responds using your keyword rules."} active={Boolean(session.autoresponse)} busy={busyAction === "autoresponse"} onToggle={() => toggleFeature("autoresponse", Boolean(session.autoresponse))} isArabic={isArabic} />
            <FeatureRow icon={BrainCircuit} title={isArabic ? "الرد الذكي" : "AI replies"} description={isArabic ? "ينشئ ردوداً باستخدام إعدادات الذكاء الاصطناعي." : "Generates replies using your AI settings."} active={Boolean(session.airesponse)} busy={busyAction === "airesponse"} onToggle={() => toggleFeature("airesponse", Boolean(session.airesponse))} isArabic={isArabic} />
          </div>
        </article>

        <article className="panel-card">
          <div className="panel-heading"><div><span className="eyebrow">{isArabic ? "التفاصيل" : "Details"}</span><h3>{isArabic ? "بيانات الاتصال" : "Connection details"}</h3></div></div>
          <dl className="details-list">
            <div><dt><Webhook size={17} />Webhook</dt><dd>{currentWebhookUrl ? <a href={currentWebhookUrl} target="_blank" rel="noreferrer" className="text-link"><Link2 size={15} />{currentWebhookUrl}</a> : (isArabic ? "غير مضاف" : "Not configured")}</dd></div>
            <div><dt><CalendarDays size={17} />{isArabic ? "تاريخ الإنشاء" : "Created"}</dt><dd>{formatDate(session.created || session.createdAt, isArabic)}</dd></div>
            <div><dt><Check size={17} />{isArabic ? "معرّف الجلسة" : "Session ID"}</dt><dd className="font-mono text-xs">{session.uuid}</dd></div>
          </dl>
        </article>
      </section>

      <section className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <article className="panel-card">
          <div className="panel-heading">
            <div>
              <span className="eyebrow">API</span>
              <h3>{isArabic ? "رمز الجلسة" : "Session token"}</h3>
            </div>
            <span className="metric-icon metric-teal"><KeyRound size={20} /></span>
          </div>
          <p className="mt-3 text-sm leading-6 text-slate-500">
            {isArabic
              ? "استخدم هذا الرمز لطلبات API من خادمك فقط. لا تشاركه أو تضعه في كود الواجهة."
              : "Use this token for server-side API requests only. Do not share it or expose it in frontend code."}
          </p>
          <div className="mt-5 flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 p-2">
            <input
              type={showToken ? "text" : "password"}
              value={sessionToken}
              readOnly
              className="min-w-0 flex-1 border-0 bg-transparent px-2 font-mono text-xs text-slate-700 outline-none"
              dir="ltr"
              aria-label={isArabic ? "رمز الجلسة" : "Session token"}
              placeholder={isArabic ? "سجل الدخول مجدداً لإظهار الرمز" : "Sign in again to load the token"}
            />
            <button
              type="button"
              className="icon-button !h-9 !w-9"
              onClick={() => setShowToken((visible) => !visible)}
              disabled={!sessionToken}
              aria-label={showToken ? (isArabic ? "إخفاء الرمز" : "Hide token") : (isArabic ? "إظهار الرمز" : "Show token")}
            >
              {showToken ? <EyeOff size={17} /> : <Eye size={17} />}
            </button>
            <button
              type="button"
              className="icon-button !h-9 !w-9"
              onClick={copySessionToken}
              disabled={!sessionToken}
              aria-label={isArabic ? "نسخ الرمز" : "Copy token"}
            >
              {tokenCopied ? <Check size={17} /> : <Copy size={17} />}
            </button>
          </div>
          {tokenCopied && <p className="mt-2 text-xs font-medium text-emerald-700" role="status">{isArabic ? "تم نسخ الرمز." : "Token copied."}</p>}
        </article>

        <article className="panel-card">
          <div className="panel-heading">
            <div>
              <span className="eyebrow">Webhook</span>
              <h3>{isArabic ? "رابط استقبال الأحداث" : "Event delivery URL"}</h3>
            </div>
            <span className={`status-badge ${webhookEnabled ? "status-badge-online" : "status-badge-offline"}`}>
              <span />{webhookEnabled ? (isArabic ? "مفعل" : "Enabled") : (isArabic ? "متوقف" : "Disabled")}
            </span>
          </div>
          <p className="mt-3 text-sm leading-6 text-slate-500">
            {isArabic
              ? "يرسل واتسي أحداث الرسائل وحالة التسليم إلى رابط HTTP أو HTTPS عام. الروابط المحلية محظورة."
              : "Whatsy sends message and delivery events to a public HTTP or HTTPS URL. Local URLs are blocked."}
          </p>

          {clientCanEditWebhook ? (
            <form className="mt-5" onSubmit={saveWebhook} noValidate>
              <label className="field-label block" htmlFor="session-webhook">Webhook URL</label>
              <input
                id="session-webhook"
                type="url"
                className="field-control mt-2 font-mono text-xs"
                value={webhookUrl}
                onChange={(event) => {
                  setWebhookUrl(event.target.value);
                  setWebhookError("");
                }}
                disabled={webhookLoading || webhookSaving}
                maxLength={255}
                placeholder="https://example.com/webhooks/whatsapp"
                dir="ltr"
                autoComplete="url"
              />
              {webhookError && <div className="notice notice-error mt-3" role="alert">{webhookError}</div>}
              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  type="submit"
                  className="primary-button primary-button-auto"
                  disabled={webhookLoading || webhookSaving || webhookUrl.trim() === savedWebhookUrl}
                >
                  {webhookSaving ? <LoaderCircle className="animate-spin" size={17} /> : <Save size={17} />}
                  {webhookSaving ? (isArabic ? "جاري الحفظ…" : "Saving…") : (isArabic ? "حفظ الرابط" : "Save URL")}
                </button>
                {savedWebhookUrl && (
                  <button type="button" className="danger-button" onClick={removeWebhook} disabled={webhookSaving}>
                    <Trash2 size={17} />{isArabic ? "حذف الرابط" : "Remove URL"}
                  </button>
                )}
              </div>
            </form>
          ) : (
            <div className="notice notice-warning mt-5">
              {isArabic ? "تعديل Webhook من هذه الشاشة متاح لحساب العميل فقط." : "Webhook editing on this screen is available to client accounts only."}
            </div>
          )}
        </article>
      </section>

      {showQr && (
        <div className="modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setShowQr(false); }}>
          <section className="modal-card" role="dialog" aria-modal="true" aria-labelledby="qr-title">
            <button type="button" className="modal-close" onClick={() => setShowQr(false)} aria-label={isArabic ? "إغلاق" : "Close"}><X size={20} /></button>
            <div className="modal-heading">
              <span className="auth-brand-mark"><PlugZap size={22} /></span>
              <div><span className="eyebrow">WhatsApp</span><h3 id="qr-title">{isArabic ? "ربط الهاتف" : "Connect your phone"}</h3></div>
            </div>
            {qrLoading ? (
              <div className="qr-state"><LoaderCircle className="animate-spin text-teal-700" size={30} /><p>{isArabic ? "جاري تجهيز رمز الربط…" : "Preparing your QR code…"}</p></div>
            ) : qrError ? (
              <div className="qr-state"><CircleAlert className="text-amber-600" size={30} /><p>{qrError}</p><button type="button" className="secondary-button" onClick={loadQr}><RefreshCw size={17} />{isArabic ? "المحاولة مجدداً" : "Try again"}</button></div>
            ) : (
              <>
                <div className="qr-code"><QRCodeSVG value={qrValue} size={224} /></div>
                <ol className="qr-steps">
                  <li>{isArabic ? "افتح واتساب على هاتفك." : "Open WhatsApp on your phone."}</li>
                  <li>{isArabic ? "اختر الأجهزة المرتبطة ثم ربط جهاز." : "Open Linked devices, then Link a device."}</li>
                  <li>{isArabic ? "امسح هذا الرمز خلال الوقت المتبقي." : "Scan this code before it refreshes."}</li>
                </ol>
                <div className="qr-timer"><span style={{ width: `${(qrTimer / 60) * 100}%` }} /><p>{isArabic ? `يتجدد الرمز خلال ${qrTimer} ثانية` : `Code refreshes in ${qrTimer} seconds`}</p></div>
              </>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
